import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';
import clsx from 'clsx';
import moment from 'moment';
import { Skeleton } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import Chart from './Chart';
import { RevenueEmpty, RevenueLoader } from '../../../../constants';
import SelectStyled from '../../../SelectStyled';
import { addCommasToNumber } from '../../../Utils/helper';

const useStyles = makeStyles(() => ({
  background__styles: {
    background: '#FFFFFF',
    borderRadius: '20px',
    height: '25rem',
  },
  space__between: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  empty__screen: {
    padding: '3rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  empty__icon: {
    height: '115px',
    width: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      width: '80%',
    },
  },
  empty__text: {
    color: '#ACC1D4',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '2rem',
    width: '75%',
  },
  header: {
    padding: '1rem 1rem 0',
  },
  heading: {
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '19px',
    color: '#6d7d8b',
    marginBottom: '.3rem',
  },
  amount: {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '35px',
    color: '#29264a',
  },
  range__dropdown: {
    cursor: 'pointer',
    background: 'rgba(239, 243, 252, 0.5)',
    borderRadius: '30px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    paddingBottom: '.58rem',
    minWidth: '140px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  range__dropdown__skeleton: {
    cursor: 'pointer',
    background: 'rgba(239, 243, 252, 0.5)',
    borderRadius: '30px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '45.55px',
    minWidth: '140px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  revenue__loader: {
    padding: '2rem 1.5rem',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
}));

export default function Revenue({ currency }) {
  const classes = useStyles();
  const [paymentAmounts, setPaymentAmounts] = useState([]);
  const [paymentDates, setPaymentDates] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [filter, setFilter] = useState('today');

  const paymentListData = useSelector(
    (state) => state.payment?.getPayments || [],
  );

  const getPaymentsLoading = useSelector(
    (state) => state.payment?.getPaymentsLoading || false,
  );

  // filtering the payment list based on today, last week and last month

  const filterPaymentList = (type) => {
    let start;
    let end;

    if (type === 'today') {
      start = moment();
      end = moment();
    } else if (type === 'sevenDays') {
      start = moment().subtract(7, 'days');
      end = moment().subtract(1, 'days');
    } else if (type === 'lastMonth') {
      // start = moment().subtract(1, 'months').startOf('month');
      // end = moment().subtract(1, 'months').endOf('month');
      start = moment().subtract(1, 'month');
      end = moment().subtract(1, 'days');
    }
    start.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    end.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    let result = _.filter(paymentListData, (p) => {
      if ((+p?.paidAmount || 0) !== 0) {
        p = moment(p.updatedAt).set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });

        return new Date(p) >= start && new Date(p) <= end;
      }
    });

    result = result.sort(
      (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    );
    return result;
  };

  /* making the date list and amount list and calculating the payment total amount
   from the payment list to pass data into Graph */

  const makingData = (list) => {
    let clonedList = _.cloneDeep(list);
    clonedList = clonedList.map((item) => {
      item.updatedAt = item.updatedAt?.split('T')?.[0] || '';
      return item;
    });

    // merging the duplicates payments and their amounts
    const result = clonedList.reduce((acc, cur) => {
      const updatedAt = cur.updatedAt;
      const found = acc.find((elem) => {
        return elem.updatedAt === updatedAt;
      });
      if (found) found.paidAmount += cur.paidAmount;
      else acc.push(cur);
      return acc;
    }, []);

    const paymentAllAmounts = [];
    const paymentAllDates = [];
    let totalPayment = 0;
    result?.map((p) => {
      paymentAllAmounts.push(
        Math.round((+p?.paidAmount || 0) * 100) / 100 || 0,
      );
      totalPayment += +p?.paidAmount;
      paymentAllDates.push(moment(p?.updatedAt).format('MMM DD'));
    });
    setTotalAmount(Math.round((totalPayment || 0) * 100) / 100 || 0);
    setPaymentDates(paymentAllDates);

    setPaymentAmounts(paymentAllAmounts);
  };

  // first time show all today payments

  useEffect(() => {
    if (paymentListData?.length > 0) {
      makingData(filterPaymentList('today'));
    }
  }, [paymentListData]);

  const handleFilter = (e) => {
    const { value } = e.target;

    setFilter(value);

    makingData(filterPaymentList(value));
  };
  return (
    <>
      <div className={classes.background__styles}>
        <div className={clsx(classes.space__between, classes.header, 'w-100')}>
          <div className={classes.heading__amount}>
            <Typography className={classes.heading}>
              {getPaymentsLoading ? <Skeleton width={75} /> : 'Total Revenue'}
            </Typography>
            {getPaymentsLoading ? (
              <Typography className={classes.amount}>
                <Skeleton width={25} />
              </Typography>
            ) : (
              <Typography className={classes.amount}>
                {' '}
                {currency}
                {addCommasToNumber(totalAmount) || '0.00'}
              </Typography>
            )}
          </div>
          {getPaymentsLoading ? (
            <div className={classes.range__dropdown__skeleton}>
              <Skeleton width={30} />
            </div>
          ) : (
            <div className={classes.range__dropdown}>
              <SelectStyled
                disabled={getPaymentsLoading}
                menus={[
                  { label: 'Today', value: 'today' },
                  { label: 'Last 7 days', value: 'sevenDays' },
                  { label: 'Last month', value: 'lastMonth' },
                ]}
                value={filter}
                onChange={handleFilter}
              />
            </div>
          )}
        </div>
        <div className={classes.content}>
          {paymentAmounts?.length < 1 && !getPaymentsLoading ? (
            <div className={classes.empty__screen}>
              <div className={classes.empty__icon}>
                <RevenueEmpty />
              </div>
              <Typography className={classes.empty__text}>
                Once your business starts booming, your revenue stats will show
                up here.
              </Typography>
            </div>
          ) : getPaymentsLoading ? (
            <div className={classes.revenue__loader}>
              <RevenueLoader />
            </div>
          ) : (
            <Chart
              series={[
                {
                  type: paymentAmounts?.length === 1 ? 'bar' : 'area',
                  name: 'Total Revenue',
                  data: paymentAmounts || [],
                },
              ]}
              options={{
                chart: {
                  toolbar: {
                    show: false,
                  },
                },
                grid: {
                  show: false,
                },
                responsive: [
                  {
                    breakpoint: 480,
                    options: {
                      chart: {
                        // height: 280,
                      },
                      legend: {
                        // position: 'bottom',
                        // offsetX: -10,
                        // offsetY: 0,
                      },
                    },
                  },
                ],
                // plotOptions for bar line
                plotOptions:
                  paymentAmounts?.length === 1
                    ? {
                        bar: {
                          horizontal: false,
                          columnWidth: '2%',
                        },
                      }
                    : {},

                dataLabels: {
                  enabled: false,
                },
                stroke: {
                  curve: 'smooth',
                  colors: ['#6921C4', '#21C46C', '#FF3E3E'],
                  width: 3,
                },

                colors: ['#DBCDF0', '#CCEEDE', '#F9D3D5'],

                fill:
                  paymentAmounts.length === 1
                    ? { type: 'solid', colors: '#6921C4', opacity: 10 }
                    : {
                        type: 'gradient',
                        gradient: {
                          shadeIntensity: 0,
                          opacityFrom: 1,
                          opacityTo: 0.5,
                        },
                      },
                yaxis: {
                  labels: {
                    formatter: function (value) {
                      return `${currency}${value?.toFixed(2)}`;
                    },
                  },
                },
                xaxis: {
                  categories: paymentDates || [],
                },
                tooltip: {
                  x: {
                    format: 'dd/MM/yy HH:mm',
                  },
                },
                legend: {
                  position: 'top',
                  horizontalAlign: 'left',
                  onItemClick: {
                    toggleDataSeries: false,
                  },
                },
                // onItemHover: {
                //   highlightDataSeries: true,
                // },
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
